import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, LogOut, ArrowLeft, Save, Loader2, Phone, Clock, Zap, Crown, Users, AlertCircle, ToggleLeft, ToggleRight, RefreshCw, Bell, Trophy, CreditCard, Coins, DollarSign } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminUrgentNotificationSettings from '../components/AdminUrgentNotificationSettings';
import AdminLeaderboardSettings from '../components/AdminLeaderboardSettings';

import { BACKEND_URL } from '../config/api';

export default function AdminOnCallSettings() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [settings, setSettings] = useState({
    on_call_enabled: true,
    launch_mode_enabled: false,
    on_call_price: 9.99,
    on_call_duration_hours: 24,
    max_duration_hours: 72,
    on_call_label_text: 'Go On Call',
    on_call_description_text: 'Make yourself available for urgent and emergency work.',
    on_call_profile_unlock_credits: 3,
    on_call_chat_start_credits: 4,
    on_call_referral_enabled: true,
    on_call_referral_days_per_referral: 30,
    employer_referral_discount_enabled: true,
    employer_referral_discount_per_referral: 5,
    employer_referral_discount_cap: 50,
    fast_response_threshold: 15,
    slow_response_threshold: 60,
    min_response_rate: 0.5,
    min_acceptance_rate: 0.3,
    downgrade_after_ignores: 3,
    governance_cooldown_hours: 24,
  });
  
  const [stats, setStats] = useState({
    active_on_call_count: 0,
    urgent_requests_today: 0,
    total_on_call_payments: 0,
    total_referral_rewards: 0,
  });

  useEffect(() => { checkAuth(); }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      if (response.data.user_type !== 'admin') { navigate('/login'); return; }
      setUser(response.data);
      fetchSettings();
    } catch { navigate('/login'); }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/on-call/admin/settings`, { withCredentials: true });
      if (response.data) {
        const { stats: s, ...rest } = response.data;
        setSettings(prev => ({ ...prev, ...rest }));
        if (s) setStats(s);
      }
    } catch { /* defaults */ } finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/on-call/admin/settings`, settings, { withCredentials: true });
      toast.success('On Call settings saved!');
    } catch { toast.error('Failed to save settings'); } finally { setSaving(false); }
  };

  const h = (field, value) => setSettings(prev => ({ ...prev, [field]: value }));

  const handleLogout = async () => {
    try { await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true }); } catch {}
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin')} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></button>
            <Phone className="h-6 w-6 text-emerald-500" />
            <span className="text-xl font-bold text-slate-900">On Call Settings</span>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-2 text-slate-600 hover:bg-slate-100 rounded-lg" data-testid="logout-btn">
            <LogOut className="h-5 w-5" /><span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={<Zap className="h-5 w-5 text-emerald-600" />} bg="bg-emerald-100" value={stats.active_on_call_count} label="Currently On Call" testId="stats-active" />
          <StatCard icon={<Phone className="h-5 w-5 text-orange-600" />} bg="bg-orange-100" value={stats.urgent_requests_today} label="Urgent Requests Today" testId="stats-requests" />
          <StatCard icon={<CreditCard className="h-5 w-5 text-blue-600" />} bg="bg-blue-100" value={stats.total_on_call_payments} label="Active Payments" testId="stats-payments" />
          <StatCard icon={<Crown className="h-5 w-5 text-amber-600" />} bg="bg-amber-100" value={stats.total_referral_rewards} label="Referral Rewards" testId="stats-rewards" />
        </div>

        {/* ─── Feature Toggle & Launch Mode ─── */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6" data-testid="access-control-section">
          <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2"><Crown className="h-5 w-5 text-amber-500" /> Feature Access Control</h2>
          
          {/* On Call Enabled */}
          <ToggleRow label="On Call Feature" description="Master toggle. When off, no tradesperson can go On Call." value={settings.on_call_enabled} onChange={() => h('on_call_enabled', !settings.on_call_enabled)} testId="on-call-enabled-toggle" />

          {/* Launch Mode */}
          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Zap className="h-5 w-5 text-amber-600" />
                  <span className="font-bold text-amber-900">Launch Mode</span>
                  {settings.launch_mode_enabled && <span className="px-2 py-0.5 bg-amber-500 text-white text-xs font-bold rounded">ACTIVE</span>}
                </div>
                <p className="text-sm text-amber-800 mb-1">When enabled, <strong>ALL tradespeople</strong> can use On Call for free (bypasses payment/referral requirement).</p>
                <p className="text-xs text-amber-700">Turn this off to enforce paid/referral access.</p>
              </div>
              <button onClick={() => h('launch_mode_enabled', !settings.launch_mode_enabled)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${settings.launch_mode_enabled ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}
                data-testid="launch-mode-toggle">
                {settings.launch_mode_enabled ? <><ToggleRight className="h-5 w-5" /> Enabled</> : <><ToggleLeft className="h-5 w-5" /> Disabled</>}
              </button>
            </div>
          </div>
        </div>

        {/* ─── Tradesperson Pricing ─── */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6" data-testid="trade-pricing-section">
          <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2"><DollarSign className="h-5 w-5 text-emerald-500" /> Tradesperson On Call Pricing</h2>
          <p className="text-sm text-slate-600 mb-4">Set the price tradespeople pay per On Call activation. This is a one-time payment for each activation period.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <NumberInput label="Price per Activation (£)" value={settings.on_call_price} onChange={v => h('on_call_price', v)} step={0.01} min={0} testId="on-call-price-input" hint="Charge per On Call activation" />
            <NumberInput label="Duration (hours)" value={settings.on_call_duration_hours} onChange={v => h('on_call_duration_hours', v)} min={1} max={168} testId="on-call-duration-input" hint="How long each activation lasts" />
            <NumberInput label="Max Duration (hours)" value={settings.max_duration_hours} onChange={v => h('max_duration_hours', v)} min={1} max={168} testId="max-duration-input" hint="Maximum allowed On Call duration" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Label Text</label>
              <input type="text" value={settings.on_call_label_text} onChange={e => h('on_call_label_text', e.target.value)} className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500" data-testid="on-call-label-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Description Text</label>
              <input type="text" value={settings.on_call_description_text} onChange={e => h('on_call_description_text', e.target.value)} className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500" data-testid="on-call-desc-input" />
            </div>
          </div>
        </div>

        {/* ─── Employer Premium Credit Costs ─── */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6" data-testid="employer-credit-section">
          <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2"><Coins className="h-5 w-5 text-orange-500" /> Employer Premium Credit Costs</h2>
          <p className="text-sm text-slate-600 mb-4">Employers spend more credits when interacting with On Call tradespeople. These rates override the standard credit costs for On Call trades.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <NumberInput label="On Call Profile Unlock (credits)" value={settings.on_call_profile_unlock_credits} onChange={v => h('on_call_profile_unlock_credits', v)} min={1} max={50} testId="on-call-unlock-credits-input" hint="Standard unlock: 1 credit" />
            <NumberInput label="On Call Chat Start (credits)" value={settings.on_call_chat_start_credits} onChange={v => h('on_call_chat_start_credits', v)} min={1} max={50} testId="on-call-chat-credits-input" hint="Standard chat: 2 credits" />
          </div>
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>Employers will see the premium cost clearly before spending credits on On Call trades.</span>
          </div>
        </div>

        {/* ─── Referral Reward Settings ─── */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6" data-testid="referral-settings-section">
          <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2"><Users className="h-5 w-5 text-purple-500" /> Referral Reward Settings</h2>
          
          {/* Tradesperson Referral */}
          <div className="mb-6">
            <h3 className="text-base font-semibold text-slate-800 mb-2">Tradesperson Referrals</h3>
            <p className="text-sm text-slate-600 mb-4">Each qualifying tradesperson referral grants the referrer a Priority Boost (higher visibility in employer searches) for a limited time. No limit on referrals.</p>
            
            <ToggleRow label="Tradesperson Referral Reward Enabled" description="Allow tradespeople to earn Priority Boost through referrals." value={settings.on_call_referral_enabled} onChange={() => h('on_call_referral_enabled', !settings.on_call_referral_enabled)} testId="referral-enabled-toggle" />
            
            {settings.on_call_referral_enabled && (
              <div className="mt-4">
                <NumberInput label="Boost Days Per Referral" value={settings.on_call_referral_days_per_referral} onChange={v => h('on_call_referral_days_per_referral', v)} min={1} max={365} testId="referral-days-per-referral-input" hint="Days of Priority Boost earned per qualifying referral (e.g., 30 = 1 month of boost)" />
              </div>
            )}
          </div>

          {/* Employer Referral Discount */}
          <div className="pt-4 border-t border-slate-200">
            <h3 className="text-base font-semibold text-slate-800 mb-2">Employer Referral Discount</h3>
            <p className="text-sm text-slate-600 mb-4">Employers earn a subscription discount for each referred employer who becomes a paying subscriber.</p>
            
            <ToggleRow label="Employer Referral Discount Enabled" description="Allow employers to earn subscription discounts through referrals." value={settings.employer_referral_discount_enabled} onChange={() => h('employer_referral_discount_enabled', !settings.employer_referral_discount_enabled)} testId="employer-discount-enabled-toggle" />
            
            {settings.employer_referral_discount_enabled && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <NumberInput label="Discount Per Referral (%)" value={settings.employer_referral_discount_per_referral} onChange={v => h('employer_referral_discount_per_referral', v)} min={1} max={50} testId="employer-discount-per-referral-input" hint="% off subscription per qualifying referral" />
                <NumberInput label="Maximum Discount Cap (%)" value={settings.employer_referral_discount_cap} onChange={v => h('employer_referral_discount_cap', v)} min={5} max={100} testId="employer-discount-cap-input" hint="Maximum total discount an employer can earn" />
              </div>
            )}
          </div>
        </div>

        {/* ─── Urgent Notifications ─── */}
        <AdminUrgentNotificationSettings />
        {/* ─── Leaderboard ─── */}
        <AdminLeaderboardSettings />

        {/* ─── Response Governance ─── */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6" data-testid="response-settings">
          <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2"><RefreshCw className="h-5 w-5 text-purple-500" /> Response Governance</h2>
          <p className="text-sm text-slate-600 mb-4">Set minimum response standards for tradespeople to maintain On Call eligibility.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <NumberInput label="Min Response Rate (%)" value={Math.round(settings.min_response_rate * 100)} onChange={v => h('min_response_rate', v / 100)} min={0} max={100} testId="min-response-rate-input" hint="Must respond to this % of urgent requests" />
            <NumberInput label="Min Acceptance Rate (%)" value={Math.round(settings.min_acceptance_rate * 100)} onChange={v => h('min_acceptance_rate', v / 100)} min={0} max={100} testId="min-acceptance-rate-input" hint="Must accept this % of urgent requests" />
            <NumberInput label="Downgrade After Ignores" value={settings.downgrade_after_ignores} onChange={v => h('downgrade_after_ignores', v)} min={1} max={10} testId="downgrade-ignores-input" hint="Consecutive ignores before cooldown" />
            <NumberInput label="Cooldown Duration (hours)" value={settings.governance_cooldown_hours} onChange={v => h('governance_cooldown_hours', v)} min={1} max={168} testId="cooldown-hours-input" hint="How long the pause lasts after downgrade" />
            <NumberInput label="Fast Response (minutes)" value={settings.fast_response_threshold} onChange={v => h('fast_response_threshold', v)} min={1} max={60} testId="fast-response-input" hint="Under this is 'fast'" />
            <NumberInput label="Slow Response (minutes)" value={settings.slow_response_threshold} onChange={v => h('slow_response_threshold', v)} min={1} max={180} testId="slow-response-input" hint="Over this is 'slow'" />
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 disabled:opacity-50"
            data-testid="save-settings-btn">
            {saving ? <><Loader2 className="h-5 w-5 animate-spin" /> Saving...</> : <><Save className="h-5 w-5" /> Save All Settings</>}
          </button>
        </div>
      </main>
    </div>
  );
}

/* ─── Reusable Sub-components ─── */

function StatCard({ icon, bg, value, label, testId }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`p-1.5 ${bg} rounded-lg`}>{icon}</div>
        <span className="text-xs text-slate-600 font-medium">{label}</span>
      </div>
      <p className="text-2xl font-bold text-slate-900">{value}</p>
    </div>
  );
}

function ToggleRow({ label, description, value, onChange, testId }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-100">
      <div><p className="font-medium text-slate-900">{label}</p><p className="text-sm text-slate-600">{description}</p></div>
      <button onClick={onChange} className={`p-2 rounded-lg transition-colors ${value ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'}`} data-testid={testId}>
        {value ? <ToggleRight className="h-6 w-6" /> : <ToggleLeft className="h-6 w-6" />}
      </button>
    </div>
  );
}

function NumberInput({ label, value, onChange, min = 0, max = 999, step = 1, testId, hint }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-2">{label}</label>
      <input type="number" value={value} onChange={e => onChange(step < 1 ? parseFloat(e.target.value) || 0 : parseInt(e.target.value) || 0)}
        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
        min={min} max={max} step={step} data-testid={testId} />
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}
